import PathCommand from "./PathCommand.js";
import Color from "./Color.js";
import Coordinate from "./Coordinate.js";
import Coordinate3D from "./Coordinate3D.js";
class Path {
    constructor(fill, stroke, strokeWidth) {
        this._commandArray = [];
        this._fill = fill !== null && fill !== void 0 ? fill : new Color();
        this._stroke = stroke !== null && stroke !== void 0 ? stroke : new Color(0, 0, 0);
        this._strokeWidth = strokeWidth !== null && strokeWidth !== void 0 ? strokeWidth : 0.1;
        this._translate = new Coordinate();
        this._scale = new Coordinate(1.0, 1.0);
        this._rotate = new Coordinate3D();
        this._pathOrigin = new Coordinate(0, 0);
        this._curPos = new Coordinate(0, 0);
        this._curCtrlPos = new Coordinate(0, 0);
        this._curQuadCtrlPos = new Coordinate(0, 0);
    }
    _setCurPos(x, y) {
        this._curPos = new Coordinate(x, y);
    }
    _setCurCtrlPos(x, y) {
        this._curCtrlPos = new Coordinate(x, y);
    }
    _setCurQuadCtrlPos(x, y) {
        this._curQuadCtrlPos = new Coordinate(x, y);
    }
    _calcSegNorm(startPos, endPos, startCtrl, endCtrl) {
        let startNorm = startCtrl.subtract(startPos).rotate(90).normalize();
        let endNorm = endCtrl.subtract(endPos).rotate(90).normalize();
        return [startNorm, endNorm];
    }
    M(x, y) {
        this._pathOrigin = new Coordinate(x, y);
        let newCommand = new PathCommand("M", [x, y]);
        this._commandArray.push(newCommand);
        this._setCurCtrlPos(x, y);
        this._setCurPos(x, y);
        this._setCurQuadCtrlPos(x, y);
    }
    m(dx, dy) { this.M(this._curPos.x + dx, this._curPos.y + dy); }
    Z() {
        this._commandArray.push(new PathCommand("Z"));
        this._setCurPos(this._pathOrigin.x, this._pathOrigin.y);
    }
    z() { this.Z(); }
    L(x, y) {
        let newCommand = new PathCommand("L", [x, y]);
        newCommand.setNormals(this._calcSegNorm(this._curPos, new Coordinate(x, y), new Coordinate(x, y), new Coordinate(2 * x, 2 * y).subtract(this._curPos)));
        newCommand.setStart(this._curPos);
        this._commandArray.push(newCommand);
        this._setCurQuadCtrlPos(this._curPos.x, this._curPos.y);
        this._setCurCtrlPos(x, y);
        this._setCurPos(x, y);
    }
    l(dx, dy) { this.L(this._curPos.x + dx, this._curPos.y + dy); }
    H(x) { this.L(x, this._curPos.y); }
    h(dx) { this.H(this._curPos.x + dx); }
    V(y) { this.L(this._curPos.x, y); }
    v(dy) { this.V(this._curPos.y + dy); }
    C(x1, y1, x2, y2, x, y) {
        let newCommand = new PathCommand("C", [x1, y1, x2, y2, x, y]);
        newCommand.setNormals(this._calcSegNorm(this._curPos, new Coordinate(x, y), new Coordinate(x1, y1), new Coordinate(x2, y2)));
        newCommand.setStart(this._curPos);
        this._commandArray.push(newCommand);
        this._setCurQuadCtrlPos(this._curPos.x, this._curPos.y);
        this._setCurCtrlPos(x2, y2);
        this._setCurPos(x, y);
    }
    c(dx1, dy1, dx2, dy2, dx, dy) {
        this.C(this._curPos.x + dx1, this._curPos.y + dy1, this._curPos.x + dx2, this._curPos.y + dy2, this._curPos.x + dx, this._curPos.y + dy);
    }
    S(x2, y2, x, y) {
        this.C(2 * this._curPos.x - this._curCtrlPos.x, 2 * this._curPos.y - this._curCtrlPos.y, x2, y2, x, y);
    }
    s(dx2, dy2, dx, dy) {
        this.S(this._curPos.x + dx2, this._curPos.y + dy2, this._curPos.x + dx, this._curPos.y + dy);
    }
    Q(x1, y1, x, y) {
        this.C((2 * x1 + this._curPos.x) / 3, (2 * y1 + this._curPos.y) / 3, (x1 * 2 + x) / 3, (y1 * 2 + y) / 3, x, y);
        this._setCurQuadCtrlPos(x1, y1);
        this._setCurCtrlPos(x, y);
        this._setCurPos(x, y);
    }
    q(dx1, dy1, dx, dy) {
        this.Q(this._curPos.x + dx1, this._curPos.y + dy1, this._curPos.x + dx, this._curPos.y + dy);
    }
    T(x, y) {
        let x1 = (this._curPos.x * 5 - this._curQuadCtrlPos.x * 2) / 3;
        let y1 = (this._curPos.y * 5 - this._curQuadCtrlPos.y * 2) / 3;
        let x2 = (x + 4 * this._curPos.x - 2 * this._curQuadCtrlPos.x) / 3;
        let y2 = (y + 4 * this._curPos.y - 2 * this._curQuadCtrlPos.y) / 3;
        let newCommand = new PathCommand("C", [x1, y1, x2, y2, x, y]);
        newCommand.setNormals(this._calcSegNorm(this._curPos, new Coordinate(x, y), new Coordinate(x1, y1), new Coordinate(x2, y2)));
        newCommand.setStart(this._curPos);
        this._commandArray.push(newCommand);
        this._setCurQuadCtrlPos(this._curPos.x * 2 - this._curQuadCtrlPos.x, this._curPos.y * 2 - this._curQuadCtrlPos.y);
        this._setCurCtrlPos(x, y);
        this._setCurPos(x, y);
    }
    t(dx, dy) { this.T(this._curPos.x + dx, this._curPos.y + dy); }
    static _pointOnEllipse(cx, cy, rx, ry, angle, sinRot, cosRot) {
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        const sx = cx + rx * cosAngle * cosRot - ry * sinAngle * sinRot;
        const sy = cy + rx * cosAngle * sinRot + ry * sinAngle * cosRot;
        return new Coordinate(sx, sy);
    }
    static _tangentVector(rx, ry, angle, sinRot, cosRot) {
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        const dx = -rx * sinAngle * cosRot - ry * cosAngle * sinRot;
        const dy = -rx * sinAngle * sinRot + ry * cosAngle * cosRot;
        return new Coordinate(dx, dy);
    }
    ;
    A(rx, ry, rotation, largeArc, sweep, x, y) {
        if (this._curPos.x === x && this._curPos.y === y) {
            return;
        }
        rx = Math.abs(rx);
        ry = Math.abs(ry);
        if (rx === 0 || ry === 0) {
            this.L(x, y);
            return;
        }
        const rot = rotation * Math.PI / 180;
        const [cosRot, sinRot] = [Math.cos(rot), Math.sin(rot)];
        const xMid = (this._curPos.x - x) / 2;
        const yMid = (this._curPos.y - y) / 2;
        const xMidTrans = cosRot * xMid + sinRot * yMid;
        const yMidTrans = -sinRot * xMid + cosRot * yMid;
        const radiiCheck = (xMidTrans * xMidTrans) / (rx * rx) + (yMidTrans * yMidTrans) / (ry * ry);
        if (radiiCheck > 1) {
            rx = Math.sqrt(radiiCheck) * rx;
            ry = Math.sqrt(radiiCheck) * ry;
        }
        const term = Math.sqrt(Math.abs((rx * rx * ry * ry - rx * rx * yMidTrans * yMidTrans - ry * ry * xMidTrans * xMidTrans) / (rx * rx * yMidTrans * yMidTrans + ry * ry * xMidTrans * xMidTrans)));
        const cSign = largeArc === sweep ? -1 : 1;
        const cxTrans = cSign * term * rx * yMidTrans / ry;
        const cyTrans = cSign * term * (-ry) * xMidTrans / rx;
        const cx = cosRot * cxTrans - sinRot * cyTrans + (this._curPos.x + x) / 2;
        const cy = sinRot * cxTrans + cosRot * cyTrans + (this._curPos.y + y) / 2;
        const startXTrans = cosRot * (this._curPos.x - cx) + sinRot * (this._curPos.y - cy);
        const startYTrans = -sinRot * (this._curPos.x - cx) + cosRot * (this._curPos.y - cy);
        const endXTrans = cosRot * (x - cx) + sinRot * (y - cy);
        const endYTrans = -sinRot * (x - cx) + cosRot * (y - cy);
        let startAngle = Math.atan2(startYTrans / ry, startXTrans / rx);
        let endAngle = Math.atan2(endYTrans / ry, endXTrans / rx);
        const angleDiff = endAngle - startAngle;
        if (!sweep && angleDiff > 0) {
            endAngle -= 2 * Math.PI;
        }
        else if (sweep && angleDiff < 0) {
            endAngle += 2 * Math.PI;
        }
        const totalArcAngle = endAngle - startAngle;
        const numSegments = Math.floor(Math.abs(totalArcAngle * 4 / Math.PI)) + 1;
        const sweepAngle = totalArcAngle / numSegments;
        let prevTangent = Path._tangentVector(rx, ry, startAngle, sinRot, cosRot);
        let prevPointOnEllipse = Path._pointOnEllipse(cx, cy, rx, ry, startAngle, sinRot, cosRot);
        for (let i = 1; i <= numSegments; i++) {
            const angle = startAngle + sweepAngle * i;
            const currentPointOnEllipse = Path._pointOnEllipse(cx, cy, rx, ry, angle, sinRot, cosRot);
            const currentTangent = Path._tangentVector(rx, ry, angle, sinRot, cosRot);
            const controlPoint = Path._lineIntersection(prevPointOnEllipse, prevTangent, currentPointOnEllipse, currentTangent);
            this.Q(controlPoint.x, controlPoint.y, currentPointOnEllipse.x, currentPointOnEllipse.y);
            prevTangent = currentTangent;
            prevPointOnEllipse = currentPointOnEllipse;
        }
    }
    a(rx, ry, rot, largeArc, sweep, dx, dy) {
        this.A(rx, ry, rot, largeArc, sweep, this._curPos.x + dx, this._curPos.y + dy);
    }
    setTranslate(x, y) {
        this._translate = new Coordinate(x, y);
    }
    setScale(x, y) {
        this._scale = new Coordinate(x, y);
    }
    setRotate(deg, x, y) {
        this._rotate = new Coordinate3D(deg, x, y);
    }
    toString(decimal) {
        return `<path fill="${this._fill}" stroke="${this._stroke}" stroke-width="${this._strokeWidth}" transform="translate(${this._translate}) scale(${this._scale}) rotate(${this._rotate})" d="${this.d(decimal)}"/>`;
    }
    d(decimal) {
        let dString = "";
        this._commandArray.forEach((c) => {
            dString += c.toString(decimal) + " ";
        });
        return dString.trim();
    }
    offset(offset) {
        if (this._commandArray[0]._command !== "M" || this._commandArray.slice(-1)[0]._command !== "Z" || this._commandArray.length < 3) {
            return;
        }
        for (let i = 1; i < this._commandArray.length - 2; i++) {
            if (this._commandArray[i]._command === "M" || this._commandArray[i]._command === "Z") {
                return;
            }
            ;
        }
        let newPath = new Path(this._fill, this._stroke, this._strokeWidth);
        let commandLength = this._commandArray.length;
        let origin = this._commandArray[commandLength - 2]._arguments.slice(-2);
        let originCoord = new Coordinate(origin[0], origin[1]);
        let lastNorm = this._commandArray[commandLength - 2]._normals[1];
        originCoord.translate(lastNorm.x, lastNorm.y);
        newPath.M(originCoord.x, originCoord.y);
        this._commandArray.forEach((p) => {
            let args = p._arguments;
            let segNorm = p._normals;
            switch (p._command) {
                case "M":
                    newPath.M(args[0], args[1]);
                    break;
                case "Z":
                    newPath.Z();
                    break;
                case "L":
                    newPath.L(p._startPoint.x + segNorm[0].x * offset, p._startPoint.y + segNorm[0].y * offset);
                    newPath.L(args[0] + segNorm[1].x * offset, args[1] + segNorm[1].y * offset);
                    break;
                case "C":
                    newPath.L(p._startPoint.x + segNorm[0].x * offset, p._startPoint.y + segNorm[0].y * offset);
                    newPath.C(args[0] + segNorm[0].x * offset, args[1] + segNorm[0].y * offset, args[2] + segNorm[1].x * offset, args[3] + segNorm[1].y * offset, args[4] + segNorm[1].x * offset, args[5] + segNorm[1].y * offset);
                    break;
                default: break;
            }
        });
        this._commandArray = newPath._commandArray;
    }
}
Path._lineIntersection = (p1, v1, p2, v2) => {
    const det = v1.x * v2.y - v1.y * v2.x;
    const t = ((p2.x - p1.x) * v2.y - (p2.y - p1.y) * v2.x) / det;
    const x = p1.x + t * v1.x;
    const y = p1.y + t * v1.y;
    return new Coordinate(x, y);
};
export default Path;
